import {ReactNode, useEffect, useState} from "react"
import {DecodedJwt, parseJwt} from "@/library/utilities/jwt"
import {apiBase} from "@/library/constants"
import {Credentials} from "@/library/api/models/auth"
import {ApiResponseBody} from "@/library/api/models/api"
import {AuthContext} from "./auth-context"

const tokenKey = 'token'

const AuthProvider = ({children}: { children?: ReactNode }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [token, setToken] = useState<string | undefined>(undefined)
    const [jwtData, setJwtData] = useState<DecodedJwt | undefined>(undefined)
    const [roles, setRoles] = useState<string[]>([])

    /**
     * Need to initialize token from localStorage if none is set, assuming one exists in local storage.
     */
    useEffect(() => {
        if (!isLoading)
            return

        setToken(() => localStorage.getItem(tokenKey) ?? undefined)
        setIsLoading(() => false)
    }, [isLoading])

    /**
     * When we have a token, we want to parse it and insert its data into the context's state.
     */
    useEffect(() => {
        if (!token || isLoading)
            return

        const parsedToken = parseJwt(token)
        setJwtData(() => parsedToken)
        setRoles(() => parsedToken?.payload?.role ?? [])
    }, [token, isLoading])

    /**
     * Update both the state and the localStorage with the token provided.
     *
     * @param token The JWT that is used for authentication, or undefined if user logged out.
     */
    const updateToken = (token?: string) => {
        setToken(() => token)

        if (!localStorage)
            return

        if (!token) localStorage.removeItem(tokenKey)
        else localStorage.setItem(tokenKey, token)
    }

    const logout = () => {
        updateToken(undefined)
    }

    const login = async (credentials: Credentials) => {
        const response = await fetch(`${apiBase}/api/auth/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(credentials)
        })

        const data: ApiResponseBody<string> = await response.json()
        if (data.hasError) {
            console.error(data.humanError)
            return Promise.reject(data.humanError)
        }

        updateToken(data.data)

        return Promise.resolve()
    }

    return <AuthContext.Provider value={{
        token: token,
        roles: roles,
        jwtData: jwtData,
        isLoading: isLoading,
        logout: logout,
        login: login,
    }}>{children}</AuthContext.Provider>
}

export default AuthProvider
